/**
 * Thread → bucket pointer document schema and paths.
 *
 * A conversation's `threadId` is globally stable (Gmail's `X-GM-THRID`, else a
 * derived RFC-822 root — see `deriveThreadId` in the functions `pollInboxLib`),
 * but the *bucket* a message lands in is chosen per message by the email
 * agent's confidence gate. Without a record of where the conversation already
 * lives, two messages that classify differently produce two thread documents in
 * two buckets, each counting one message, with no link between them — and
 * because bucket membership is an access boundary, the two halves also have
 * different reader sets.
 *
 * This collection is that record: one pointer document per conversation, keyed
 * on the conversation id, naming the bucket the conversation is committed to.
 * `processEmail` claims it transactionally on the conversation's first message
 * and follows it for every message after, so the conversation stays whole.
 *
 * Two properties are load-bearing and should survive any future edit:
 *
 * - **It is only ever read by known id.** That is why it needs no Firestore
 *   composite index, which matters concretely because this repo creates indexes
 *   by hand (see `src/tooling/firebase-config/README.md`). A change that turns
 *   this into a query is a signal to reconsider, not to add an index.
 * - **It is a top-level collection, not a subcollection of a bucket.** The
 *   pointer must be reachable before a bucket is chosen, so it cannot be nested
 *   under one. Being top-level also keeps it outside the `reports/{bucketId}`
 *   match in `firestore.rules`.
 *
 * Server-written only, and read only by the admin SDK, which bypasses rules —
 * so the deny-by-default rules already cover it and there is no client-SDK
 * mirror of this module.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';

/**
 * Base schema for a pointer document at `threadIndex/{threadId}`.
 *
 * Deliberately minimal: it answers exactly one question ("which bucket does
 * this conversation live in?"), so anything derivable from the thread document
 * itself belongs there instead.
 */
export const ThreadIndexDocumentSchema = z
  .object({
    /**
     * Bucket the conversation is committed to — a course id, or the bulk
     * bucket id (`UNCATEGORIZED_BUCKET_ID` in `../reportBucket`). This is the
     * bucket every message of the conversation is routed into, regardless of
     * how an individual message classifies.
     */
    bucketId: z.string(),
    /**
     * Server time the pointer was last written. Today that is only ever the
     * time it was claimed, because the pointer is never rewritten once it
     * exists; the field exists so a future re-routing pass can record when it
     * moved a conversation.
     */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link ThreadIndexDocumentSchema}. */
export type ThreadIndexDocument = z.infer<typeof ThreadIndexDocumentSchema>;

/** Top-level collection holding one thread → bucket pointer per conversation. */
export const THREAD_INDEX_COLLECTION = 'threadIndex' as const;

/**
 * Thread-index collection path.
 *
 * @returns Firestore collection path `threadIndex`.
 * @throws Never (string construction only).
 */
export function getThreadIndexCollectionPath(): string {
  return THREAD_INDEX_COLLECTION;
}

/**
 * Single thread-index pointer document path.
 *
 * @param args.threadId - The (sanitized) conversation id, the same id used for
 *   the thread document under its bucket.
 * @returns Firestore doc path `threadIndex/{threadId}`.
 * @throws Never (string construction only).
 */
export function getThreadIndexDocumentPath({
  threadId,
}: {
  threadId: string;
}): string {
  return `${getThreadIndexCollectionPath()}/${threadId}`;
}
